import { message, Modal } from 'antd';
import {
  useSetCurrentConversationIdValue,
  useMakeConversationCache,
  useGetConversationInfo,
  useCurrentConversationIdValue,
  LocalMessage,
  useDelConversationById,
  getFailCauseMsg,
} from 'hooks/business/conversation';
import { useEffect, useState, createContext, useContext, useRef, FC } from 'react';
import { v4 as uuidv4 } from 'uuid';
import posthog from '@leyan/lytics';
import notification from 'services/notification';
import nimInst, { NimInst } from 'services/nim';
import { NimSystemACKMessage, NIM_LOGIN_SIGNAL } from 'services/nim/eventBus';
import {
  useComingMsgToConversation,
  useSysFailMsgToConversation,
  ConversationMsg,
  useReadStatusMsgToConversation,
} from 'hooks/business/im/useMsgToConversation';
import { useIsSbsMode } from 'hooks/business/useIsSbs';
import { useTranslate, TFnType } from '@tolgee/react';
import useSystem, {
  useGetNotificationPermission,
  useSystemLanguageValue,
} from 'hooks/business/system/useSystem';
import logout from 'utils/logout';
import chatttSDK from 'services/chattt';
import focusChatttClient from 'utils/focusChatttClient';
import {
  useConversationReadTimeMapValue,
  useSetConversationReadTimeMapValue,
} from 'hooks/business/useConversationReadTime';
import { throttle, uniqBy } from 'lodash-es';
import { useConversationRead } from 'hooks/business/useConversationRead';
import {
  useAssistantReadTimeMapStateValue,
  useOfficialReadTimeMapStateValue,
  useSetAssistantReadTimeMapStateValue,
  useSetOfficialReadTimeMapStateValue,
} from 'hooks/business/im/timeMap';
import { UploadStateChangeEvent } from 'hooks/business/sendMessage';
import { useLocation, useNavigate } from 'react-router-dom';
import { InfoCircleOutlined } from '@ant-design/icons';
import toLowerCase from 'utils/toLowerCase';
import { useStoreNameConfig, isStoreAliasFirst } from 'hooks/business/store/useStoreName';
import { detail } from 'services/api/review';
import { getInsertIndex, isValidReview, useGlobalReview } from 'views/Review/hooks/useNewReview';
import {
  useSetBatchForwardLoading,
  useResetBatchForwardConvIds,
  useBatchForwardConvIds,
  useSetBatchForwardEnable,
  useBatchForwardIsDelConv,
} from 'hooks/business/conversationsHooks/useConversationBatchForward';
import { useFlagConfigValue } from 'hooks/business/system/useFlagConfig';
import { isAeProduct } from 'productLine';
import aNotification, { close as aNotificationClose } from './aNotification';
import { addResourceMessage } from 'services/cache/resourceMessage';
import { useSetConversation, useGetConversationById } from 'hooks/business/lruConversation';
import {
  ClickViewDescription,
  NegativeReviewNotificationDescription,
  NegativeReviewNotificationMessage,
} from 'components/NotificationComponent';
import { useAuthorization } from 'components/Authorization';
import { openTikTokReviewNotification } from 'components/TikTokReviewNotification';
import { useCountryCodeValue } from 'hooks/business/system/useCountryCode';
import { patchKwaiMsgResource, updateUserHangRecover } from 'services/api/conversation';
import { NIMOnDisconnectResult } from '@yxim/nim-web-sdk/dist/types/chatroom/types';
import { sendLoginConfirm } from 'services/nim/toSystem/sendConvOpe';
import { isDefaultHangUp, isIdEqual } from 'utils/is';
import { formatLoggerData, logger } from 'services/logger';
import { useCheckQAPair } from 'hooks/business/im/useCheckQAPair';
import { useBotMessageHandler } from './useBotMessageHandler';
import { eventBus } from 'services/eventBus';
import { getInfoFromKnowledgeQueryExtra } from 'utils/getItemInfo';
import {
  useMessageList,
  useGetMessageById,
  useUpdateMessage,
  useBatchUpdateMessages,
  useReplaceMessage,
} from 'hooks/business/message';
import getNimCacheId from 'hooks/business/im/getNimCacheId';
import { useMemoizedFn } from 'ahooks';
import { unstable_batchedUpdates } from 'react-dom';
// import { nimEventReport } from './nimReport';

export type NimStatus =
  | 'Idle'
  | 'Pending'
  | 'Connected'
  | 'Disconnected'
  | 'ReConnecting'
  | 'Error';

export interface NimContextValue {
  on: NimInst['on'];
  send: NimInst['send'];
  sendSystem: NimInst['sendSystem'];
}

const { login, ...nimInstWithoutLogin } = nimInst;

export const NimContext = createContext<NimContextValue>(nimInstWithoutLogin);
export const NimStatusContext = createContext<NimStatus>('Idle');

const { Provider: NimInstProvider } = NimContext;
const { Provider: NimStatusProvider } = NimStatusContext;

export interface NimContextProps {
  token?: string;
  account?: string;
  appKey?: string;
  orgId?: Oversea.Token.Payload['orgId'];
  userId?: Oversea.Token.Payload['id'];
  jwtToken: string;
}

// 不需要连接云信的路由
const isNotConnectNimPathname = (pathname: string) => {
  return pathname.includes('/sbs') || pathname.includes('auth/shop/result');
};

/** 监听消息,把最新消息同步到会话标签上 */
export function useIMCommingMessage() {
  const im = useContext(NimContext);
  const { on } = im;
  const comingMsgToConversation = useComingMsgToConversation();
  const sysFailMsgToConversation = useSysFailMsgToConversation();
  const readStatusMsgToConversation = useReadStatusMsgToConversation();

  const setConversationMap = useSetConversation();
  const getConversation = useGetConversationById();

  const messageList = useMessageList();
  const updateMessage = useUpdateMessage();
  const batchUpdateMessage = useBatchUpdateMessages();

  const setConversationReadTimeMap = useSetConversationReadTimeMapValue();

  const officialReadTimeMap = useOfficialReadTimeMapStateValue();

  const setOfficialReadTimeMap = useSetOfficialReadTimeMapStateValue();

  const assistantReadTimeMap = useAssistantReadTimeMapStateValue();

  const setAssistantReadTimeMap = useSetAssistantReadTimeMapStateValue();

  const setBatchForwardLoading = useSetBatchForwardLoading();
  const resetBatchForwardConvIds = useResetBatchForwardConvIds();
  const batchForwardConvIds = useBatchForwardConvIds();
  const setBatchForwardEnable = useSetBatchForwardEnable();
  const batchForwardIsDelConv = useBatchForwardIsDelConv();
  const delConversationById = useDelConversationById();

  const { t } = useTranslate();
  const nav = useNavigate();
  const location = useLocation();
  const { config: storeNameConfig } = useStoreNameConfig();

  const { systemSetting } = useSystem();

  const {
    platformInfo: reviewPlatformInfo,
    shopeeSearch: reviewShopeeSearch,
    lazadaSearch: reviewLazadaSearch,
    mercadoSearch: reviewMercadoSearch,
    tiktokSearch: reviewTikTokSearch,
    tiktokUSSearch: reviewTikTokUsSearch,
    followedStoreList,
    setMissingReviewListMap,
    setReviewList,
    setCurrentReview,
    setReviewStat,
  } = useGlobalReview();

  const refsCurrent = {
    comingMsgToConversation,
    sysFailMsgToConversation,
    readStatusMsgToConversation,
    messageList,
    updateMessage,
    batchUpdateMessage,
    setConversationReadTimeMap,
    setConversationMap,
    getConversation,
    t,
    officialReadTimeMap,
    setOfficialReadTimeMap,
    assistantReadTimeMap,
    setAssistantReadTimeMap,
    setReviewList,
    setCurrentReview,
    nav,
    location,
    storeNameConfig,
    systemSetting,
    reviewPlatformInfo,
    reviewShopeeSearch,
    reviewLazadaSearch,
    reviewMercadoSearch,
    reviewTikTokSearch,
    reviewTikTokUsSearch,
    followedStoreList,
    setMissingReviewListMap,
    setReviewStat,
  };
  const refs = useRef(refsCurrent);

  const setConversation = useMakeConversationCache('set');

  refs.current = refsCurrent;

  useEffect(() => {
    const offs = [
      /** 消息(包括系统消息) */
      on('message', (message: Conversation.Message) => {
        if (
          message &&
          message.conversationId &&
          !(message as unknown as UploadStateChangeEvent).resourceId
        ) {
          const {
            current: { comingMsgToConversation },
          } = refs;

          comingMsgToConversation(message as ConversationMsg);
        }
      }),
      /** 以下 sys: 开头为系统通知,不进消息列表展示 */
      on('sys:message:assign_fail', (data) => {
        const {
          content: { conversationId },
        } = data;

        const {
          current: { sysFailMsgToConversation },
        } = refs;

        sysFailMsgToConversation(conversationId);
      }),
      on('sys:message:unread_count', (data) => {
        const {
          current: { readStatusMsgToConversation },
        } = refs;

        readStatusMsgToConversation(data);
      }),
      on('sys:message:read_event', (data) => {
        const {
          content: { conversationId: readConversationId, readTime },
        } = data;

        const {
          current: { setConversationReadTimeMap, batchUpdateMessage, messageList },
        } = refs;

        setConversationReadTimeMap((map) => {
          return {
            ...map,
            [`${readConversationId}`]: readTime,
          };
        });

        const needUpdateMessageMap: Record<string, Conversation.Message> = {};

        for (let i = messageList.length - 1; i >= 0; i -= 1) {
          const { [i]: message } = messageList;

          const { msgId, conversationId, sendTime, readFlag } = message;

          if (!isIdEqual(conversationId, readConversationId)) {
            // eslint-disable-next-line no-continue
            continue;
          }

          if (readFlag) {
            break;
          }

          if (parseInt(sendTime as string, 10) <= readTime) {
            needUpdateMessageMap[msgId] = {
              ...message,
              readFlag: true,
            };
          }
        }

        if (Object.keys(needUpdateMessageMap).length > 0) {
          const entries = Object.entries(needUpdateMessageMap);
          batchUpdateMessage(entries);
        }
      }),
      on('sys:message:follower_event', (data) => {
        const {
          content: { conversationId: id, follower },
        } = data;
        const conversationId = `${id}`;

        const {
          current: { updateMessage, comingMsgToConversation, t },
        } = refs;

        unstable_batchedUpdates(() => {
          const conversation = getConversation(conversationId);
          if (conversation) {
            setConversationMap(conversationId, {
              ...conversation,
              followerFlag: follower,
            });
          }

          const tempMsgId = `${uuidv4()}`;

          const msg = {
            msgId: tempMsgId,
            conversationId: `${conversationId}`,
            storeId: tempMsgId,
            fromId: tempMsgId,
            fromUserName: tempMsgId,
            toId: tempMsgId,
            toUserName: tempMsgId,
            messageType: 'SYS_OUT',
            sysOutContent: {
              text: t('ViewsChat.followerFailed'),
            },
            status: 'SUCCESS',
            msgFromType: 'SYSTEM',
            channel: 'LAZADA',
            region: 'ID',
            extra: tempMsgId,
            externalId: tempMsgId,
            yunXinMsgId: tempMsgId,
            sendTime: Date.now(),
            isSupervise: false,
            msgResource: 'CHAT',
            tunnelAccountId: conversation?.tunnelAccountId ?? '0',
          } as const;

          updateMessage(tempMsgId, msg);

          comingMsgToConversation(msg);
        });
      }),
      on('sys:message:official_read_mark', (data) => {
        const {
          content: { conversationId, status, timestamp },
        } = data;

        const stringConversationId = `${conversationId}`;

        const {
          current: { officialReadTimeMap, setOfficialReadTimeMap, setConversationMap },
        } = refs;

        const { [stringConversationId]: preReadTime } = officialReadTimeMap;

        if (preReadTime && preReadTime > timestamp) {
          return;
        }

        unstable_batchedUpdates(() => {
          setOfficialReadTimeMap((map) => {
            return {
              ...map,
              [stringConversationId]: timestamp,
            };
          });

          const conversation = getConversation(stringConversationId);
          if (conversation) {
            setConversationMap(stringConversationId, {
              ...conversation,
              officialRead: status === 'mark_as_read',
            });
          }
        });
      }),
      on('sys:message:assistant_read_mark', (data) => {
        const {
          content: { conversationId, status, timestamp },
        } = data;

        const stringConversationId = `${conversationId}`;

        const {
          current: { assistantReadTimeMap, setAssistantReadTimeMap, setConversationMap },
        } = refs;

        const { [stringConversationId]: preReadTime } = assistantReadTimeMap;

        if (preReadTime && preReadTime > timestamp) {
          return;
        }

        unstable_batchedUpdates(() => {
          setAssistantReadTimeMap((map) => {
            return {
              ...map,
              [stringConversationId]: timestamp,
            };
          });

          const conversation = getConversation(stringConversationId);
          if (conversation) {
            setConversationMap(stringConversationId, {
              ...conversation,
              assistantRead: status === 'mark_as_read',
            });
          }
        });
      }),
      on('sys:message:remind_scene', (data) => {
        const {
          content: { conversationId, scene, hint },
        } = data;

        const {
          current: { setConversationMap, getConversation },
        } = refs;

        const stringConversationId = `${conversationId}`;

        unstable_batchedUpdates(() => {
          const conversation = getConversation(stringConversationId);
          if (conversation) {
            const { remindScenes: preRemindScenes = [] } = conversation;

            setConversationMap(stringConversationId, {
              ...conversation,
              remindMark: true,
              remindScenes: [
                ...preRemindScenes,
                {
                  remindSceneName: scene,
                  remindSceneHint: JSON.stringify(hint),
                },
              ],
            });
          }
        });
      }),
      on('sys:message:remind_solve', (data) => {
        const {
          content: { conversationId, processed },
        } = data;

        const {
          current: { setConversationMap, getConversation },
        } = refs;

        const stringConversationId = `${conversationId}`;

        unstable_batchedUpdates(() => {
          const conversation = getConversation(stringConversationId);
          if (conversation) {
            setConversationMap(stringConversationId, {
              ...conversation,
              processed,
              remindMark: false,
              remindScenes: [],
            });
          }
        });
      }),
      on('sys:message:message_event_stream', (data) => {
        const {
          content: { eventType, platformType, region, content },
        } = data;

        const {
          current: {
            systemSetting: {
              messageRemind: { refundRemind = true },
            },
          },
        } = refs;

        if (eventType === 'COMMENT' || refundRemind === false) {
          return;
        }

        const pathMap = {
          RETURN_ORDER: '/refund/return',
          IN_CANCEL_ORDER: '/refund/cancel',
          COMMENT: '/comment',
        };
        let name = '';
        if (location.pathname.includes('/refund/')) {
          return;
        }
        try {
          const { storeName, storeAlias, storeAliasName } = JSON.parse(content);
          const alias = storeAlias || storeAliasName;
          name =
            isStoreAliasFirst(storeNameConfig, platformType, region) && alias ? alias : storeName;
        } catch (e) {
          //
        }
        const message = `${toLowerCase(platformType)} ${t(
          `region.${toLowerCase(region)}`,
        )} ${name} ${t('notification.你有一个新的x通知', {
          msgType: t(`notification.${eventType}`),
        })}`;
        const key = `open${Date.now()}`;
        aNotification({
          duration: 0,
          message,
          icon: <InfoCircleOutlined style={{ color: '#f1a40c' }} />,
          description: <ClickViewDescription />,
          key,
          onClick: () => {
            nav(pathMap[eventType], {
              state: data,
            });
            aNotificationClose(key);
          },
        });
      }),
      on('sys:message:message_event_stream', (data) => {
        const {
          content: { eventType, platformType, region, content },
        } = data;

        if (eventType !== 'COMMENT') {
          return;
        }

        refs.current.setReviewStat((pre) => {
          return {
            totalNum: pre.totalNum + 1,
            shopeeNum: pre.shopeeNum + (platformType === 'SHOPEE' ? 1 : 0),
            lazadaNum: pre.lazadaNum + (platformType === 'LAZADA' ? 1 : 0),
            mercadoNum: pre.mercadoNum + (platformType === 'MERCADO' ? 1 : 0),
            tiktokNum: pre.tiktokNum + (platformType === 'TIKTOK' ? 1 : 0),
            tiktokUsNum: pre.tiktokUsNum + (platformType === 'TIKTOK_US' ? 1 : 0),
          };
        });

        try {
          const {
            commentId,
            storeName,
            storeNice,
          }: {
            commentId: string;
            storeName: string;
            storeNice: string;
          } = JSON.parse(content);

          detail({
            commentId,
          })
            .then((detail) => {
              const {
                id,
                channel,
                buyerName: userName,
                buyerPicture: userPicture,
                buyerComment,
                status,
                resultStar: star,
                sellerRating,
                productRating,
                logisticsRating,
                commentCreateTime,
                imageUrls,
                videoUrls,
                handleName: receptionName,
              } = detail;

              const sellerStar = channel === 2 ? `${sellerRating}` : '';
              const productStar = channel === 2 ? `${productRating}` : '';
              const logisticsStar = channel === 2 ? `${logisticsRating}` : '';

              const newReview: Review.Profile = {
                id,
                commentId,
                userName,
                userPicture,
                status,
                star,
                sellerStar,
                productStar,
                logisticsStar,
                buyerComment,
                commentCreateTime,
                imageUrls,
                videoUrls,
                receptionName,
              };

              const {
                current: {
                  setReviewList,
                  location,
                  storeNameConfig,
                  reviewPlatformInfo: { currentPlatform },
                  reviewShopeeSearch,
                  reviewLazadaSearch,
                  reviewMercadoSearch,
                  reviewTikTokSearch,
                  reviewTikTokUsSearch,
                  followedStoreList,
                  setMissingReviewListMap,
                  systemSetting: {
                    messageRemind: { reviewRemind = true },
                  },
                },
              } = refs;

              if (!currentPlatform) {
                return;
              }

              const search = {
                SHOPEE: reviewShopeeSearch,
                LAZADA: reviewLazadaSearch,
                MERCADO: reviewMercadoSearch,
                TIKTOK: reviewTikTokSearch,
                TIKTOK_US: reviewTikTokUsSearch,
              }[currentPlatform];

              const isValid = isValidReview(newReview, detail, search, followedStoreList);

              if (isValid) {
                setReviewList((pre) => {
                  const index = getInsertIndex(newReview, search.cursor[0] || {}, pre.list);

                  return {
                    ...pre,
                    list: uniqBy(
                      index < 0
                        ? [...pre.list, newReview]
                        : [...pre.list.slice(0, index), newReview, ...pre.list.slice(index)],
                      'commentId',
                    ),
                  };
                });
              } else {
                setMissingReviewListMap((pre) => {
                  return {
                    ...pre,
                    [platformType]: [...pre[platformType], newReview],
                  };
                });
              }

              if (location.pathname === '/review' || reviewRemind === false) {
                return;
              }

              const name =
                isStoreAliasFirst(storeNameConfig, platformType, region) && storeNice
                  ? storeNice
                  : storeName;

              aNotification({
                key: commentId,
                duration: 0,
                icon: <InfoCircleOutlined style={{ color: '#f1a40c' }} />,
                message: <NegativeReviewNotificationMessage name={name} />,
                description: (
                  <NegativeReviewNotificationDescription
                    onClick={() => {
                      const {
                        current: { nav, setCurrentReview },
                      } = refs;

                      nav('/review');

                      if (isValid) {
                        setCurrentReview(newReview);
                      }

                      aNotificationClose(commentId);
                    }}
                  />
                ),
              });
            })
            .catch(() => {
              message.error(t('review.评价详情加载失败'));
            });
        } catch (err) {
          //
        }
      }),
      on('sys:message:remove_talk_control', (data) => {
        const {
          content: { conversationId },
        } = data;

        unstable_batchedUpdates(() => {
          const conversation = getConversation(conversationId);
          if (conversation) {
            setConversationMap(conversationId, {
              ...conversation,
              controlStatus: false,
              receptionName: '',
            });
          }
        });
      }),
      on('sys:message:batch_transfer', (data) => {
        setBatchForwardLoading(false);
        const {
          content: { success, failItem },
        } = data;
        const total = batchForwardConvIds.length;

        posthog.capture('$batchTransferConversationResult', {
          ...data,
        });

        if (success && (!failItem || failItem?.length === 0)) {
          message.success(t('forward.batchSuccess', { total }));
          setBatchForwardEnable(false);
          resetBatchForwardConvIds();
          const ids = [...batchForwardConvIds];
          if (batchForwardIsDelConv) {
            // 删除会话
            delConversationById(ids);
          }
        } else {
          const failTotal = failItem?.length || 0;
          message.success(
            t('forward.batchFailed', {
              successTotal: total - failTotal,
              failTotal,
            }),
          );
          const failedIds = failItem.map((i) => i.conversationId.toString());
          const successIds = batchForwardConvIds.filter((id) => !failedIds.includes(id.toString()));
          resetBatchForwardConvIds(failedIds);

          if (batchForwardIsDelConv) {
            // 删除会话
            delConversationById(successIds);
          }
        }
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [
    batchForwardConvIds,
    batchForwardIsDelConv,
    delConversationById,
    location.pathname,
    nav,
    on,
    t,
    resetBatchForwardConvIds,
    setBatchForwardEnable,
    setBatchForwardLoading,
    setConversation,
    setConversationMap,
    getConversation,
    storeNameConfig,
  ]);
}

const unprocessedAck: Record<string, NimSystemACKMessage['content']> = {};
const getNotifyContent = (
  message: Conversation.Message,
  storeList: Conversation.RegionStoreInfo[],
  t: TFnType,
) => {
  const { messageType, fromUserName, textContent, storeId } = message;
  let content: string | undefined;
  const targetStore = storeList.find((x) => x.storeId === storeId);
  const storeName = targetStore?.storeName ?? t('notification.NEW_MESSAGE_REMINDER');

  switch (messageType) {
    case 'SYS_PAUSE_AI_START':
    case 'SYS_PAUSE_AI_STOP':
    case 'SYS_PAUSE_AI_EXPIRE':
    case 'SYS_PAUSE_MK_RR_START':
    case 'SYS_PAUSE_MK_RR_STOP':
    case 'SYS_PAUSE_MK_RR_EXPIRE':
    case 'SYS_TRANSFER': {
      content = `[${t('notification.SYSTEM_MESSAGE')}]`;
      break;
    }
    case 'TEXT': {
      content = `${fromUserName}:${textContent?.text}`;
      break;
    }
    case 'IMAGE':
    case 'ITEM':
    case 'ITEM_LIST':
    case 'OFFER':
    case 'LAZADA_OFFER':
    case 'ORDER':
    case 'VIDEO':
    case 'VOUCHER':
    case 'FAQ_LIVEAGENT':
    case 'BUNDLE_MESSAGE':
    case 'STICKER':
    case 'SYS_OUT':
    case 'FOLLOWER':
    case 'NONSUPPORT': {
      content = t(
        messageType === 'IMAGE' || messageType === 'VOUCHER'
          ? 'notification.content1'
          : 'notification.content2',
        { sender: fromUserName, msgType: t(`messageType.${messageType}`) },
      );
      break;
    }

    default:
      // 感觉没必要，但是又不想直接删掉 default 分支。
      content = undefined;
  }
  return {
    title: storeName,
    content,
  };
};

type Notify = typeof notification.notify;

const createNotify = (interval: number) => {
  const fn: Notify = (...props) => notification.notify(...props);

  return interval === 0 ? fn : throttle<Notify>(fn, interval, { leading: true, trailing: true });
};

const notifyRef: { current: Notify } = {
  current: createNotify(5 * 1000),
};

export const updateNotifyInterval = (interval: number) => {
  notifyRef.current = createNotify(interval);
};

export const Nim: FC = ({ children }) => {
  const { payload, token: jwtToken } = useAuthorization<Oversea.Token.Payload>();
  const account = payload?.accid;
  const token = payload?.nimToken;
  const userId = payload?.id;
  const orgId = payload?.orgId;
  const appKey = import.meta.env.VITE_NIM_APPKEY as string;

  const props = {
    value: {
      account,
      token,
      userId,
      orgId,
      appKey,
      jwtToken,
    },
    children,
  };

  const [status, setStatus] = useState<NimStatus>('Idle');
  const getNotificationPermission = useGetNotificationPermission();
  const location = useLocation();

  const { t } = useTranslate();

  const isSbsMode = useIsSbsMode();
  const { nimDb, nimDbLog, nimLogLevel } = useFlagConfigValue();
  const countryCode = useCountryCodeValue();

  const botMessageHandler = useBotMessageHandler();

  const sendNimLoginConfirm = () => {
    const assistantId = payload?.id;
    if (assistantId) {
      setTimeout(() => {
        sendLoginConfirm(assistantId);
      }, 2000);

      setTimeout(() => {
        sendLoginConfirm(assistantId);
      }, 5000);
    }
  };

  useIMCommingMessage();
  useEffect(() => {
    // sp 模式下不链接nim
    if (token && account && appKey && orgId && userId) {
      // /sbs 打单报告页面时,不占用云信通道
      if (isNotConnectNimPathname(location.pathname) || isSbsMode) {
        return;
      }

      /**
       * 验证巴西云信连接问题
       */
      let defaultLink;
      if (countryCode !== 'CN') {
        defaultLink = 'weblinkoutsea01.netease.im:443';
      } else {
        defaultLink = ['weblink.netease.im', 'weblinkoutsea01.netease.im:443'];
      }
      const logLevel = countryCode === 'BR' ? 'debug' : nimLogLevel;

      (isDefaultHangUp(orgId)
        ? updateUserHangRecover('HANG').catch(() => {})
        : Promise.resolve()
      ).then(() => {
        nimInst.login({
          token,
          account,
          appKey,
          // 目前不依赖云信提供的本地数据库，应该主动关闭，以减少indexdb io对应用性能的影响
          db: nimDb || false,
          dbLog: nimDbLog || false,
          quickReconnect: true,
          needReconnect: true,
          logLevel,
          defaultLink,
          orgId,
          userId,
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, account, appKey, isSbsMode, orgId, userId]);

  useEffect(() => {
    let waitForReconnectTimeout: ReturnType<typeof setTimeout> | undefined;

    /**
     * 若云信在时限内有执行reconnect，可以取消自动connect
     */
    const stopForceReconnect = () => {
      if (waitForReconnectTimeout) {
        clearTimeout(waitForReconnectTimeout);
      }
    };
    const offs = [] as Array<() => any>;
    offs.push(
      nimInst.on(NIM_LOGIN_SIGNAL, () => {
        setStatus('Pending');
        // const loadingTips = message.loading('会话系统连接中', 0)
      }),
    );
    offs.push(
      nimInst.on('connect', () => {
        stopForceReconnect();
        if (!isAeProduct) {
          message.success(t('nim.connect'));
        }
        setStatus('Connected');
        getNotificationPermission();
        sendNimLoginConfirm();
      }),
    );
    offs.push(
      nimInst.on('willReconnect', () => {
        stopForceReconnect();
        if (!isAeProduct) {
          message.info(t('nim.willReconnect'));
        }
        setStatus('ReConnecting');
      }),
    );
    offs.push(
      nimInst.on('disconnect', (p: NIMOnDisconnectResult) => {
        message.error(t('nim.disconnect'));
        if (
          p.code === 302 ||
          p.code === 403 ||
          p.code === 417 ||
          p.code === 422 ||
          p.code === 'kicked'
        ) {
          logout('kicked');
        }
        setStatus('Disconnected');
      }),
    );
    return () => {
      offs.forEach((off) => off());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, getNotificationPermission, userId]);

  const sysLang = useSystemLanguageValue();

  const setCurId = useSetCurrentConversationIdValue();
  const curId = useCurrentConversationIdValue();

  const updateMessage = useUpdateMessage();
  const replaceMessage = useReplaceMessage();
  const getMessageById = useGetMessageById();
  const messageList = useMessageList();

  const conversationReadTimeMap = useConversationReadTimeMapValue();

  const setConversation = useMakeConversationCache('set');
  const getConversationInfo = useGetConversationInfo(true);
  const conversationRead = useConversationRead();
  const { replaceQAPairMsgAfterAck } = useCheckQAPair();
  const getConversation = useGetConversationById();

  const refsCurrent = {
    setCurId,
    conversationReadTimeMap,
    getConversationInfo,
    setConversation,
    curId,
    conversationRead,
    t,
    sysLang,
    userId,
    replaceQAPairMsgAfterAck,
    botMessageHandler,
    updateMessage,
    replaceMessage,
    getMessageById,
    messageList,
    getConversation,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  /**
   * 收到 ack 时更新会话
   */
  const updateConversationWhenAck = useMemoizedFn(
    async (
      conversationId: string,
      success: boolean,
      updateMsgSendTime: string | number,
      failCause: Conversation.FailContent | undefined,
    ) => {
      const conversation = await getConversationInfo(conversationId);
      if (!conversation) {
        return;
      }

      const isOnCurrentConversation = isIdEqual(conversation.conversationId, curId);
      if (isOnCurrentConversation) {
        refs.current.conversationRead(conversation);
      }

      if (
        updateMsgSendTime >= conversation.lastMsgTime ||
        !success ||
        conversation.channel === 'WA_BIZ' ||
        conversation.mockExternal
      ) {
        /**
         * @see https://leyan.yuque.com/czpqnb/pd/iy6xom#LNkCR
         *  失败类型为D的消息视为已处理。
         */
        const unreadCount =
          success || isOnCurrentConversation || failCause?.failType === 'D'
            ? conversation.unreadCount
            : 1;
        refs.current.setConversation({
          ...conversation,
          processed: success || failCause?.failType === 'D',
          fstUnprocessedMsgTimestamp: success ? '0' : conversation.fstUnprocessedMsgTimestamp,
          failCause,
          mockExternal: Boolean(failCause && conversation.mockExternal),
          unreadCount,
        });
      }
    },
  );

  useEffect(() => {
    const offs = [
      nimInst.on('sys:message:ack', (data) => {
        const { content } = data;
        const { msgId, yunXinMsgId, success } = content;

        if (!yunXinMsgId) {
          return;
        }

        const failCause: Conversation.FailContent | undefined = !success
          ? content.failCause
          : undefined;

        const { replaceMessage, getMessageById, messageList, curId, t, sysLang, userId } =
          refs.current;

        const nimId = getNimCacheId(yunXinMsgId);
        // yunXinMsgId 是前端发送消息的时候生成的
        // 场景： 客服发送了一条消息，推送给超管，此时消息发送失败了，给超管推送了个ack
        // 由于超管本地没有 nimId 的缓存，因此只能通过 yunXinMsgId来筛选出是哪一条消息的ack
        const target =
          getMessageById(nimId) || messageList.find((x) => x.yunXinMsgId === yunXinMsgId);
        if (!target) {
          unprocessedAck[yunXinMsgId] = content;
          return;
        }

        if (target.messageType === 'TTS_LOGISTICS_CARD') {
          posthog.capture(success ? '$sendTtsLogisticsCardSuccess' : '$sendTtsLogisticsCardFailed');
        }

        if (failCause?.code === 'LE_CHAT_DETECTED_CENSORED_MSG') {
          const { conversationId, fromId } = target;
          if (conversationId === curId && `${fromId}` === `${userId}`) {
            Modal.info({
              title: t('send_msg_failed'),
              content: getFailCauseMsg(sysLang, failCause),
            });
          }
        }

        const localMsgId = target.msgId;
        const serverMsgId = `${msgId}`;
        const cachedMessage = getMessageById(localMsgId);

        if (cachedMessage) {
          updateConversationWhenAck(
            cachedMessage.conversationId,
            success,
            cachedMessage.sendTime,
            failCause,
          );
          const updatedMsg: LocalMessage = {
            ...cachedMessage,
            status: success ? 'SUCCESS' : 'FAIL',
            msgId: `${msgId}`,
            localStatus: success ? 'synced' : 'error',
            failCause,
          };
          // 原子化替换：删除旧消息 + 添加新消息，只触发一次渲染，避免 UI 闪烁
          replaceMessage(localMsgId, serverMsgId, updatedMsg);
          requestAnimationFrame(() => {
            refs.current.replaceQAPairMsgAfterAck(nimId, updatedMsg);
          });
        }
      }),
      nimInst.on('sys:message:ack_timeout', (data) => {
        const {
          content: { yunXinMsgId },
        } = data;
        const nimId = getNimCacheId(yunXinMsgId);

        updateMessage(nimId, (prev) => {
          if (!prev) {
            return;
          }
          return {
            ...prev,
            localStatus: 'ack_timeout',
          };
        });
      }),
      nimInst.on('sys:message:tiktok_shop_offline', () => {
        openTikTokReviewNotification();
      }),
      nimInst.on('sys:message:update_mark', (message) => {
        const {
          content: { conversationId, talkId, addMark, delMark },
        } = message;

        refs.current
          .getConversationInfo(conversationId.toString())
          .then((conversation) => {
            if (!talkId || conversation.talkId === talkId.toString()) {
              const botMark = Boolean(((conversation.botMark ? 1 : 0) | addMark) & ~delMark & 1);
              if (botMark !== conversation.botMark) {
                refs.current.setConversation({
                  ...conversation,
                  botMark,
                });
              }
            }
          })
          .catch(() => {});
      }),
      nimInst.on('message', async (message: Conversation.Message | null) => {
        if (!message) {
          return;
        }

        if ((message as any).resourceId) {
          addResourceMessage((message as any).resourceId, message);
          logger.info(
            `[ResourceUpload]: ${JSON.stringify({
              type: 'yunxinMessage',
              data: message,
            })}`,
          );

          return;
        }

        await patchKwaiMsgResource(message);

        const { updateMessage, conversationReadTimeMap, setCurId, t } = refs.current;
        const { msgId, conversationId, sendTime, localStatus } = message as LocalMessage;
        const { [conversationId]: readTime } = conversationReadTimeMap;
        const ackContent = unprocessedAck[message.yunXinMsgId];

        if (localStatus === 'loading' && ackContent) {
          const { msgId, success, failCause } = ackContent;
          delete unprocessedAck[message.yunXinMsgId];
          updateMessage(`${msgId}`, (preMessage) => {
            const info = message.extra ? getInfoFromKnowledgeQueryExtra(message.extra) : undefined;
            return {
              ...preMessage,
              ...message,
              ...info,
              localStatus: success ? 'synced' : 'error',
              failCause,
              readFlag: readTime >= parseInt(sendTime as string, 10),
            };
          });
        } else {
          updateMessage(`${msgId}`, (preMessage) => {
            const info = message.extra ? getInfoFromKnowledgeQueryExtra(message.extra) : undefined;
            return {
              ...preMessage,
              ...message,
              ...info,
              readFlag: readTime >= parseInt(sendTime as string, 10),
            };
          });
        }

        const { lastMsgTime } = refs.current.getConversation(message.conversationId) || {};
        if (Number(message.sendTime) < (Number(lastMsgTime) || 0)) {
          return;
        }
        const { title, content } = getNotifyContent(message, [], t);

        const isToReviewMessage =
          message.messageType === 'SYS_ASSIGN' &&
          message.sysAssignContent?.assignType === 'TO_REVIEW';

        if (['SYSTEM', 'BUYER'].includes(message.msgFromType) && !isToReviewMessage && content) {
          if (chatttSDK?.emitter?.emit) {
            chatttSDK?.emitter.emit('new_message', message);
          }
          notifyRef.current(
            {
              title,
              content,
              id: message.msgId,
              conversationId: message.conversationId,
            },
            {
              onClick: () => {
                focusChatttClient();
                setCurId(message.conversationId);
              },
            },
          );
        }

        refs.current.botMessageHandler(message);
      }),
      nimInst.on('sys:message:knowledge_query_clear', (data) => {
        const { content } = data;
        if (content && content.conversationId) {
          eventBus.emit('QAPAIR_CONV_KNOWLEDGE_QUERY_REFETCH', content.conversationId);
        }
      }),
      nimInst.on('sys:message:knowledge_query', (data) => {
        const { content } = data;
        logger.info(
          `receiveKnowledgeQuery ${formatLoggerData({
            conversationId: content?.conversationId ?? '',
            queryMsgId: content?.msgId ?? '',
            timestamp: Date.now().toString(),
          })}`,
        );
        if (content && content.conversationId) {
          eventBus.emit('QAPAIR_CONV_KNOWLEDGE_QUERY_REFETCH', content.conversationId);
        }
      }),
    ];
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NimInstProvider value={nimInstWithoutLogin}>
      <NimStatusProvider {...props} value={status} />
    </NimInstProvider>
  );
};

// export const useNim = () => {
//   return useContext(NimContext);
// };

export const useNimStatus = () => {
  return useContext(NimStatusContext);
};

export default Nim;
